/* global window console */

import { DEBUG, MOAT } from "./bakingConstants.js";
import { getPinsForMoving, getWeight } from "../../helpers/grid.js";

export function generateDrawingCommands(
  left,
  top,
  width,
  height,
  gridd,
  cellsCoords,
  iconCache,
  shapeCache,
  styleSheet
) {
  const drawingCommands = [];
  let drawIndex = 0;

  if (DEBUG) {
    Object.keys(cellsCoords).forEach((key) => {
      const cell = cellsCoords[key];
      drawingCommands.push({ type: "rect", ...cell, order: drawIndex++, tag: "Debug" });
    });
  }

  try {
    gridd.structure.cells.forEach((row, ri) => {
      row.forEach((cell, ci) => {
        const cellCoords = cellsCoords[cell.id];
        const cellStyle = gridd.cellStyles[cell.id];

        if (isUnderMerged(gridd, ri, ci)) {
          // no need to render this cell as it is covered by another one
          return;
        }

        //console.debug("Baking cell", { cell, cellCoords, cellStyle });

        if (!cellStyle) {
          // no style info given for the cell, we can't draw
          return;
        }

        if (!cellCoords) {
          console.error("This cell's coords are not calculated, skipping drawing command.", { cell, cellStyle });
          return;
        }

        if (cellStyle.background || cellCoords?.zIndex > 0) {
          //console.log("rendering background", { cellStyle, cellCoords, cell });
          if (cellStyle.background === "transparent" && !(cellCoords?.zIndex > 0)) {
            // transparent background is not working in PPT, skipping creating rect
          } else {
            drawingCommands.push({
              type: "rect",
              left: cellCoords.left,
              top: cellCoords.top,
              width: cellCoords.width,
              height: cellCoords.height,
              fill: getMergedBackground(cellCoords, cellStyle),
              radius: 0,
              order: drawIndex++ + safeParseInt(cellCoords?.zIndex) + 4000,
              tag: "Background_" + cell.id,
            });
          }
        }

        if (cellStyle.img) {
          const fixedContent = cellStyle.img.content.startsWith("data:")
            ? cellStyle.img.content.slice(cellStyle.img.content.indexOf(","))
            : cellStyle.img.content.startsWith("data:image/webp;base64,")
              ? cellStyle.img.content.slice(23)
              : cellStyle.img.content.startsWith("data:image/png;base64,")
                ? cellStyle.img.content.slice(22)
                : cellStyle.img.content;

          // TODO: apply custom spacing values
          // TODO: apply ratio keeping
          drawingCommands.push({
            type: "image",
            tag: "Image_" + cell.id,
            data: fixedContent,
            left: cellCoords.left,
            top: cellCoords.top,
            width: cellCoords.width,
            height: cellCoords.height,
            order: drawIndex++ + safeParseInt(cellCoords?.zIndex) + 6000,
          });
        }

        if (cellStyle.shapeGroupId) {
          const grouped = cellCoords.shape.grouped;
          const shapeZIndex =
            drawIndex++ + safeParseInt(cellCoords?.zIndex) + safeParseInt(cellStyle?.shape?.zIndex) + 5000;

          console.log("Drawing multi-cell shape", { cellStyle, cell, grouped });
          const preparedCellShapeStyle = {
            fill: cellStyle.shape?.color,
            fontColor: cellStyle.color,
            outline:
              cellStyle.shape?.outline && cellStyle.shape.outline !== "none"
                ? {
                    style: cellStyle.shape.outline,
                    weight: cellStyle.shape.outlineWeight,
                    color: cellStyle.shape.outlineColor,
                  }
                : undefined,
          };

          if (grouped.myLevel === 0) {
            if (grouped.horizontal) {
              const shapeWidth = cellCoords.shape.width - grouped.margin - grouped.gap;
              drawingCommands.push({
                type: "triangle",
                tag: "Pyramid_Cap_" + cell.id,
                left:
                  cellCoords.shape.left +
                  cellCoords.shape.width / 2 -
                  grouped.d -
                  (grouped.reverse ? grouped.gap : -grouped.gap),
                top: cellCoords.shape.top + cellCoords.shape.height / 2 - shapeWidth / 2,
                width: grouped.d * 2,
                height: shapeWidth,
                rotation: grouped.reverse ? 90 : -90,
                ...preparedCellShapeStyle,
                order: shapeZIndex,
              });
            } else {
              drawingCommands.push({
                type: "triangle",
                tag: "Pyramid_Cap_" + cell.id,
                left: cellCoords.shape.left + cellCoords.shape.width / 2 - grouped.d,
                top: cellCoords.shape.top + grouped.margin,
                width: grouped.d * 2,
                height: cellCoords.shape.height - grouped.margin - grouped.gap,
                rotation: grouped.reverse ? 180 : 0,
                ...preparedCellShapeStyle,
                order: shapeZIndex,
              });
            }
          } else {
            if (grouped.horizontal) {
              const shapeWidth = cellCoords.shape.width - grouped.bg - grouped.gap;
              drawingCommands.push({
                type: "trapezoid",
                tag: "Pyramid_Level_" + grouped.myLevel + "_" + cell.id,
                left: cellCoords.shape.left + cellCoords.shape.width / 2 - grouped.db,
                top: cellCoords.shape.top + cellCoords.shape.height / 2 - shapeWidth / 2,
                width: grouped.db * 2,
                height: shapeWidth,
                angle: grouped.gamma + Math.PI / 2 / 180, // in radian the top-left angle of the trapezoid
                rotation: grouped.reverse ? 90 : -90,
                ...preparedCellShapeStyle,
                order: shapeZIndex,
              });
            } else {
              drawingCommands.push({
                type: "trapezoid",
                tag: "Pyramid_Level_" + grouped.myLevel + "_" + cell.id,
                left: cellCoords.shape.left + cellCoords.shape.width / 2 - grouped.db,
                top: cellCoords.shape.top + grouped.gap,
                width: grouped.db * 2,
                height: cellCoords.shape.height - grouped.bg - grouped.gap,
                angle: grouped.gamma + Math.PI / 2 / 180, // in radian the top-left angle of the trapezoid
                rotation: grouped.reverse ? 180 : 0,
                ...preparedCellShapeStyle,
                order: shapeZIndex,
              });
            }
          }
        } else if (cellStyle.shape) {
          const cellShape = cellStyle.shape;
          const shapeZIndex = drawIndex++ + safeParseInt(cellCoords?.zIndex) + safeParseInt(cellShape?.zIndex) + 5000;
          const rotationAdd = (cellShape.rotation ?? 0) + (cellShape.flipX ? 180 : 0) + (cellShape.flipY ? 180 : 0);
          const preparedCellShapeStyle = {
            fill: cellShape.color,
            fontColor: cellStyle.color,
            outline:
              cellShape.outline && cellShape.outline !== "none"
                ? {
                    style: cellShape.outline,
                    weight: cellShape.outlineWeight,
                    color: cellShape.outlineColor,
                  }
                : undefined,
            transparency: cellShape.transparency,
            shadow: cellShape.shadow,
          };
          // TODO: shape to svg drawing command (except: rect, rounded rect, circle, ellipse)
          if (cellStyle.shape.type === "RoundedRectangle") {
            drawingCommands.push({
              type: "rect",
              tag: "Rect_" + cell.id,
              radius: cellShape.radius,
              left: cellCoords.shape.left,
              top: cellCoords.shape.top,
              width: cellCoords.shape.width,
              height: cellCoords.shape.height,
              ...preparedCellShapeStyle,
              order: shapeZIndex,
              rotation: rotationAdd,
            });
          } else if (cellStyle.shape.type === "RoundedThinRectangle") {
            drawingCommands.push({
              type: "rect",
              tag: "Rect_" + cell.id,
              radius: cellShape.radius,
              left: cellCoords.shape.left,
              top: cellCoords.shape.top + cellCoords.shape.height * 0.25,
              width: cellCoords.shape.width,
              height: cellCoords.shape.height * 0.5,
              ...preparedCellShapeStyle,
              order: shapeZIndex,
              rotation: rotationAdd,
            });
          } else if (cellStyle.shape.type === "Circle") {
            if (cellCoords.shape.width > cellCoords.shape.height) {
              drawingCommands.push({
                type: "ellipse",
                tag: "Circle_" + cell.id,
                left: cellCoords.shape.left + cellCoords.shape.width / 2 - cellCoords.shape.height / 2,
                top: cellCoords.shape.top,
                width: cellCoords.shape.height,
                height: cellCoords.shape.height,
                ...preparedCellShapeStyle,
                order: shapeZIndex,
              });
            } else {
              drawingCommands.push({
                type: "ellipse",
                tag: "Circle_" + cell.id,
                left: cellCoords.shape.left,
                top: cellCoords.shape.top + cellCoords.shape.height / 2 - cellCoords.shape.width / 2,
                width: cellCoords.shape.width,
                height: cellCoords.shape.width,
                ...preparedCellShapeStyle,
                order: shapeZIndex,
                rotation: rotationAdd,
              });
            }
          } else if (cellStyle.shape.type === "Oval") {
            drawingCommands.push({
              type: "ellipse",
              tag: "Ellipse_" + cell.id,
              left: cellCoords.shape.left,
              top: cellCoords.shape.top,
              width: cellCoords.shape.width,
              height: cellCoords.shape.height,
              ...preparedCellShapeStyle,
              order: shapeZIndex,
              rotation: rotationAdd,
            });
          } else if (cellStyle.shape.type === "ArrowFull") {
            if (rotationAdd === 90 || rotationAdd === -90 || rotationAdd === 270) {
              drawingCommands.push({
                type: "chevron",
                tag: "Chevron_" + cell.id,
                left: cellCoords.shape.left + cellCoords.shape.width / 2 - cellCoords.shape.height / 2,
                top: cellCoords.shape.top + cellCoords.shape.height / 2 - cellCoords.shape.width / 2,
                width: cellCoords.shape.height,
                height: cellCoords.shape.width,
                angle: 0.3,
                ...preparedCellShapeStyle,
                order: shapeZIndex,
                rotation: rotationAdd,
              });
            } else {
              drawingCommands.push({
                type: "chevron",
                tag: "Chevron_" + cell.id,
                left: cellCoords.shape.left,
                top: cellCoords.shape.top,
                width: cellCoords.shape.width,
                height: cellCoords.shape.height,
                angle: 0.3,
                ...preparedCellShapeStyle,
                order: shapeZIndex,
                rotation: rotationAdd,
              });
            }
          } else if (cellStyle.shape.type === "ArrowWithoutTail") {
            let height = cellCoords.shape.height;
            let top = cellCoords.shape.top;
            if (rotationAdd === 90 || rotationAdd === -90 || rotationAdd === 270) {
              height = cellCoords.shape.width / 2;
            }
            if (rotationAdd === -90 || rotationAdd === 270) {
              top = cellCoords.shape.top - cellCoords.shape.height / 2;
            }

            drawingCommands.push({
              type: "thinchevron",
              tag: "ChevronThin_" + cell.id,
              left: cellCoords.shape.left + cellCoords.shape.width / 2 - 16,
              top,
              width: 24,
              height,
              angle: 0.6,
              ...preparedCellShapeStyle,
              order: shapeZIndex,
              rotation: rotationAdd,
            });
          } else if (cellStyle.shape.type === "LongTriangle") {
            drawingCommands.push({
              type: "triangle",
              tag: "Triangle_" + cell.id,
              left: cellCoords.shape.left,
              top: cellCoords.shape.top,
              width: cellCoords.shape.width,
              height: cellCoords.shape.height,
              ...preparedCellShapeStyle,
              order: shapeZIndex,
              rotation: rotationAdd,
            });
          } else if (cellStyle.shape.type === "ArrowThin") {
            if (rotationAdd === 90 || rotationAdd === -90 || rotationAdd === 270) {
              drawingCommands.push({
                type: "triangle",
                tag: "ArrowThin_" + cell.id,
                left: cellCoords.shape.left,
                top: cellCoords.shape.top + (rotationAdd === 90 ? cellCoords.shape.height / 2 : 0),
                width: cellCoords.shape.width,
                height: cellCoords.shape.height / 2,
                ...preparedCellShapeStyle,
                order: shapeZIndex,
                rotation: -90 - rotationAdd,
              });
            } else {
              const height = 32;
              const width = cellCoords.shape.height;
              drawingCommands.push({
                type: "triangle",
                tag: "ArrowThin_" + cell.id,
                left: cellCoords.shape.left + cellCoords.shape.width / 2 - width / 2,
                top: cellCoords.shape.top + cellCoords.shape.height / 2 - height / 2,
                width,
                height,
                ...preparedCellShapeStyle,
                order: shapeZIndex,
                rotation: 90 + rotationAdd,
              });
            }
          } else if (cellStyle.shape.type === "Triangle") {
            drawingCommands.push({
              type: "triangle",
              tag: "Triangle_" + cell.id,
              left: cellCoords.shape.left + cellCoords.shape.width * 0.4,
              top: cellCoords.shape.top,
              width: cellCoords.shape.width * 0.2,
              height: cellCoords.shape.height,
              ...preparedCellShapeStyle,
              order: shapeZIndex,
              rotation: rotationAdd,
            });
          } else if (cellStyle.shape.type === "Trapeze1") {
            drawingCommands.push({
              type: "trapezoid",
              tag: "Trapeze1_" + cell.id,
              left: cellCoords.shape.left + cellCoords.shape.width * 0.3,
              top: cellCoords.shape.top,
              width: cellCoords.shape.width * 0.4,
              height: cellCoords.shape.height,
              angle: 0.3,
              ...preparedCellShapeStyle,
              order: shapeZIndex,
              rotation: rotationAdd,
            });
          } else if (cellStyle.shape.type === "Trapeze2") {
            drawingCommands.push({
              type: "trapezoid",
              tag: "Trapeze2_" + cell.id,
              left: cellCoords.shape.left + cellCoords.shape.width * 0.2,
              top: cellCoords.shape.top,
              width: cellCoords.shape.width * 0.6,
              height: cellCoords.shape.height,
              angle: 0.3,
              ...preparedCellShapeStyle,
              order: shapeZIndex,
              rotation: rotationAdd,
            });
          } else if (cellStyle.shape.type === "Trapeze3") {
            drawingCommands.push({
              type: "trapezoid",
              tag: "Trapeze3_" + cell.id,
              left: cellCoords.shape.left + cellCoords.shape.width * 0.1,
              top: cellCoords.shape.top,
              width: cellCoords.shape.width * 0.8,
              height: cellCoords.shape.height,
              angle: 0.3,
              ...preparedCellShapeStyle,
              order: shapeZIndex,
              rotation: rotationAdd,
            });
          } else if (cellStyle.shape.type === "ArrowCommon") {
            drawingCommands.push({
              type: "arrow",
              tag: "Arrow_" + cell.id,
              left: cellCoords.shape.left,
              top: cellCoords.shape.top,
              width: cellCoords.shape.width,
              height: cellCoords.shape.height,
              ...preparedCellShapeStyle,
              order: shapeZIndex,
              rotation: rotationAdd,
            });
          } else if (cellStyle.shape.type === "CurvedArrow") {
            const side = Math.min(cellCoords.shape.width, cellCoords.shape.height);
            drawingCommands.push({
              type: "circularArrow",
              tag: "CurvedArrow_" + cell.id,
              left:
                (side === cellCoords.shape.width
                  ? cellCoords.shape.left
                  : cellCoords.shape.left + (cellCoords.shape.width - cellCoords.shape.height) / 2) +
                side / 2,
              top:
                (side === cellCoords.shape.height
                  ? cellCoords.shape.top
                  : cellCoords.shape.top + (cellCoords.shape.height - cellCoords.shape.width) / 2) +
                side / 2,
              width: side,
              height: side,
              angle: 270,
              rotation: -90 + rotationAdd,
              ...preparedCellShapeStyle,
              order: shapeZIndex,
            });
          } else if (cellStyle.shape.type === "Quarter") {
            drawingCommands.push({
              type: "quarter",
              tag: "Quarter_" + cell.id,
              left: cellCoords.shape.left,
              top: cellCoords.shape.top,
              width: cellCoords.shape.width,
              height: cellCoords.shape.height,
              angle: -90,
              angleB: -360,
              ...preparedCellShapeStyle,
              order: shapeZIndex,
              rotation: rotationAdd,
            });
          } else if (shapeCache["shape_" + cell.id]) {
            drawingCommands.push({
              type: "svg",
              order: shapeZIndex,
              tag: "GeneralShape_" + cell.id,
              data: shapeCache["shape_" + cell.id],
              left: cellCoords.shape.left,
              top: cellCoords.shape.top,
              width: cellCoords.shape.width,
              height: cellCoords.shape.height,
              color: cellShape.color,
            });
          }
        }

        if (cellStyle.icon) {
          if (cellStyle.iconEnclosure !== "none" && !!cellStyle.iconEnclosure) {
            drawingCommands.push({
              type: "ellipse",
              order: drawIndex++ + safeParseInt(cellCoords?.zIndex) + 7000,
              tag: "Icon_Filled_" + cell.id,
              left: (cellCoords.icon.left ?? 0) - (4 + (cellCoords.icon.width ?? 24) * 0.1),
              top: (cellCoords.icon.top ?? 0) - (4 + (cellCoords.icon.height ?? 24) * 0.1),
              width: (cellCoords.icon.width ?? 24) + (4 + (cellCoords.icon.width ?? 24) * 0.1) * 2,
              height: (cellCoords.icon.height ?? 24) + (4 + (cellCoords.icon.height ?? 24) * 0.1) * 2,
              fill: cellStyle.iconEnclosure === "filled" ? cellStyle.iconColor : undefined,
              outline: {
                color: cellStyle.iconEnclosure !== "none" ? cellStyle.iconColor : undefined,
                weight: cellStyle.iconEnclosure !== "none" ? 2 : undefined,
              },
            });
          }
          const iconData = iconCache[cellStyle.icon];
          let manipulatedIconData =
            cellStyle.iconEnclosure !== "filled"
              ? iconData?.replace('stroke="#FFFFFF"', `stroke="${cellStyle.iconColor}"`)
              : iconData;
          drawingCommands.push({
            type: "svg",
            order: drawIndex++ + 4000 + safeParseInt(cellCoords?.zIndex),
            tag: "Icon_" + cell.id,
            data: manipulatedIconData,
            left: cellCoords.icon.left ?? 0,
            top: cellCoords.icon.top ?? 0,
            width: cellCoords.icon.width ?? 24,
            height: cellCoords.icon.height ?? 24,
            color: cellStyle.iconColor,
          });
          // console.log("Transformed icon to drawing command", {
          //   cell,
          //   cellCoords,
          //   cellStyle,
          //   iconData,
          //   manipulatedIconData,
          // });
        }

        const cleanedText =
          cell.text
            ?.replace(/<[^>]+>/g, "")
            ?.replace(/(?:\n\n)/g, "\n")
            ?.trim() ?? "";

        if (cleanedText && cleanedText !== "") {
          const text = {
            type: "text",
            id: cell.id,
            tag: `${ri}x${ci}`,
            left: cellCoords.text.left,
            top: cellCoords.text.top,
            width: cellCoords.text.width,
            height: cellCoords.text.height,
            margins: {
              left: cellCoords.text?.margin?.left,
              right: cellCoords.text?.margin?.right,
              top: cellCoords.text?.margin?.top,
              bottom: cellCoords.text?.margin?.bottom,
            },
            text: cleanedText,
            order: drawIndex++ + safeParseInt(cellCoords?.zIndex) + safeParseInt(cellCoords?.shape?.zIndex) + 6000,
            fontFamily: cellStyle.fontFamily,
            fontSize: cellStyle.size,
            color: cellStyle.color,
            horizontalAlign: cellStyle.textAlign,
            verticalAlign: cellStyle.verticalAlign,
            bold: cellStyle.weight === "bold",
            italic: cellStyle.style === "italic",
            underline: cellStyle.fontUnderlined === "underline",
            spacing: {
              paragraph: styleSheet?.lineSpacing?.find((ls) => ls.id === cellStyle.size)?.paragraph ?? 0,
              line: styleSheet?.lineSpacing?.find((ls) => ls.id === cellStyle.size)?.line ?? 1.3,
            },
          };

          text.subStrings = [];
          console.log("Prepare text substring operations", { cell });
          let location = 0;
          cell.json?.content?.forEach((paragraph) => {
            paragraph.content?.forEach((line) => {
              if (line.type === "text") {
                const sb = parseLine(line, location, text.text?.length ?? 0);
                if (sb) text.subStrings.push(sb);
                location += line.text?.length ?? 0;
              } else if (line.type === "listItem") {
                line.content.forEach((p) => {
                  console.log("List item paragraphs", p);
                  p.content.forEach((txt) => {
                    const from = Math.min(location, (text.text?.length ?? 0) - 1);
                    const length = Math.min(txt.text?.length ?? 0, (text.text?.length ?? 0) - from);
                    console.warn("substring calculation result", { location, from, length, txt, text });
                    text.subStrings.push({
                      from,
                      length,
                      bullet: paragraph.type === "bulletList",
                      numbered: paragraph.type === "orderedList",
                    });
                    const sb = parseLine(txt, location);
                    if (sb) text.subStrings.push(sb);
                    location += length;
                  });
                  location += 1;
                });
              }
            });
            location += 1;
          });

          drawingCommands.push(text);
        }
      });
    });

    const colPins = getPinsForMoving(gridd.structure.columns, window.innerWidth);
    const rowPins = getPinsForMoving(gridd.structure.rows, window.innerHeight);
    gridd.borders.forEach((border) => {
      // TODO: move this calculations to the calculateShapeCoords.js file and focus in this file to translate to drawingCommands

      // adding to the left coordinate the width of the vertical borders preceding this horizontal border
      const bl =
        MOAT +
        left +
        (border.isVertical
          ? colPins[border.index ?? 0] +
            (border.index > 0
              ? cellsCoords.borderWeights.cols.slice(0, border.index - 1).reduce((a, n) => (a += n), 0)
              : 0)
          : colPins[border.from ?? 0]);

      // adding to the top coordinate the height of the horizontal borderes preceding this vertical border
      const bt =
        MOAT +
        top +
        (border.isVertical
          ? rowPins[border.from ?? 0]
          : rowPins[border.index ?? 0] +
            (border.index > 0
              ? cellsCoords.borderWeights.rows.slice(0, border.index - 1).reduce((a, n) => (a += n), 0)
              : 0));

      // adding the width of the vertical borders to the width of this border if it is horizontal
      const width = border.isVertical
        ? 0.001
        : colPins[Math.min(colPins.length - 1, border.to ?? colPins.length - 1)] -
          colPins[border.from ?? 0] +
          cellsCoords.borderWeights.cols
            .slice(border.from ?? 0, Math.min(colPins.length - 1, border.to ?? colPins.length - 1))
            .reduce((a, n) => (a += n), 0);

      // adding the height of the horizontal borders to the height for this border if it is vertical
      const height = border.isVertical
        ? rowPins[Math.min(rowPins.length - 1, border.to ?? rowPins.length - 1)] -
          rowPins[border.from ?? 0] +
          cellsCoords.borderWeights.rows
            .slice(border.from ?? 0, Math.min(rowPins.length - 1, border.to ?? rowPins.length - 1))
            .reduce((a, n) => (a += n), 0)
        : 0.001;

      // 0.001 as height or width keeps the line straight
      // 0 does not work as it translates to 7200% scale instead
      // 0.1 translates to 10% scale of 0 which is closer but not perfect to straight lines
      // undefined or null results to overscale value as above

      if (Number.isNaN(bl) || Number.isNaN(bt) || Number.isNaN(width) || Number.isNaN(height)) {
        console.error("Border coordinates are improper, skipping drawing command", {
          bl,
          bt,
          width,
          height,
          border,
          top,
          left,
          colPins,
          rowPins,
        });
      } else {
        drawingCommands.push({
          type: "border",
          id: border.id,
          left: bl,
          top: bt,
          width,
          height,
          color: border.color,
          weight: border.weight,
          style: border.style,
          order: drawIndex++ + (border.isVertical ? 2000 : 0),
        });
      }
    });

    // Column spacers
    gridd.structure.columns.forEach((col, i) => {
      if (col[0] === "s") {
        const sl = MOAT + left + colPins[i] + cellsCoords.borderWeights.cols.slice(0, i).reduce((a, n) => (a += n), 0);
        const st = top;
        drawingCommands.push({
          type: "rect",
          id: "Spacer_Col_" + i,
          left: sl,
          top: st,
          width: getWeight(col),
          height,
          color: gridd.spacerStyles.columnColors ? gridd.spacerStyles.columnColors[i] : "white",
          order: drawIndex++ + 1000,
        });
      }
    });

    // Row spacers
    gridd.structure.rows.forEach((row, i) => {
      if (row[0] === "s") {
        // TODO: move this calculations to the calculateShapeCoords.js file and focus in this file to translate to drawingCommands
        const sl = left;
        const st = MOAT + top + rowPins[i] + cellsCoords.borderWeights.rows.slice(0, i).reduce((a, n) => (a += n), 0);
        drawingCommands.push({
          type: "rect",
          id: "Spacer_Row_" + i,
          left: sl,
          top: st,
          width,
          height: getWeight(row),
          color: gridd.spacerStyles.rowColors ? gridd.spacerStyles.rowColors[i] : "white",
          order: drawIndex++ + 3000,
        });
      }
    });

    //console.log("Baking commands original order", { drawingCommands, gridd });

    // Depth sorting before actual drawing
    return drawingCommands.sort((a, b) => {
      //console.log("sorting", { ao: a.order, bo: b.order, a, b });
      return a.order - b.order;
    });
  } catch (error) {
    console.error("Baking preparation failed", { error, drawingCommands, drawIndex, cellsCoords });
    throw error;
  }
}

function isUnderMerged(gridd, ri, ci) {
  return gridd.structure.merged.find(
    (m) =>
      (m.dir === "down" && m.cell.y === ci && m.cell.x < ri && ri < m.cell.x + 1 + m.length) ||
      (m.dir === "right" && m.cell.x === ri && m.cell.y < ci && ci < m.cell.y + 1 + m.length)
  );
}

function safeParseInt(n) {
  //console.log("parsing safe int", { n });
  return parseInt(n ?? "0");
}

function getMergedBackground(cellCoords, cellStyle) {
  if (cellCoords?.zIndex > 0) {
    if (
      cellStyle.background === "none" ||
      cellStyle.background === "transparent" ||
      cellStyle.background === null ||
      cellStyle.background === undefined
    ) {
      return "#FFFFFF";
    }
  }
  return cellStyle.background;
}

function parseLine(line, location, maxLength) {
  const marks = line.marks?.map((m) => m.type);
  const from = Math.min(location, maxLength - 1);
  const length = Math.min(line.text?.length ?? 0, maxLength - from);
  if (marks?.filter((m) => ["bold", "italic", "underline"].includes(m))?.length > 0) {
    return {
      from,
      length,
      bold: marks.includes("bold"),
      italic: marks.includes("italic"),
      underline: marks.includes("underline"),
    };
  }
}
